// The play's PICK, on its own, for the redesigned results rows (#2004): the prominent
// row title is what RAS bet — the team + line (sides) or the game total — plus the
// matchup, with no rotation number. The rotation, score, close and CLV live in the muted
// meta line beside it, so the title stays a clean, self-evident pick.
//
// A side names its opponent ("Chiefs -3 vs Jaguars"): a bare team + line says nothing
// about who it was against, and the opponent is most of what makes a result readable
// months later. The preposition carries the venue the way a bettor writes it — `vs` when
// the pick is at home, `at` when it is on the road — so no separate location is needed.
// Totals name BOTH teams for the same reason, joined by "/" since a total is a property
// of the whole game rather than a pick against someone.
//
// Contrast with formatReleaseLine (the legacy one-line label), which leads with the
// rotation number.
//
// Names come in two widths (`nameMode`): 'short' is the ESPN abbreviation (KC, ALA) —
// tight enough for mobile; 'long' is the full team name for desktop, except NFL where
// the abbreviation and the city are both redundant, so we use the nickname alone
// (Baltimore Ravens → Ravens). Callers render both and swap by breakpoint.

const NFL_SPORT_ID = 3;

type PickRelease = Partial<
  Pick<
    API.Release,
    | 'sportId'
    | 'playType'
    | 'sideIndicator'
    | 'side'
    | 'totalIndicator'
    | 'total'
    | 'period'
    | 'price'
    | 'visitor'
    | 'home'
    | 'playText'
  >
>;

export type NameMode = 'short' | 'long';

/** The NFL nickname: the last word of a "City Nickname" name (Kansas City Chiefs → Chiefs). */
const nickname = (name?: string) => name?.trim().split(/\s+/).pop() || '';

function teamLabel(team: Partial<API.Team> | undefined, mode: NameMode, sportId?: number): string {
  if (!team) {
    return '';
  }
  if (mode === 'short') {
    return team.shortName || team.name || '';
  }
  if (sportId === NFL_SPORT_ID && team.name) {
    return nickname(team.name);
  }
  return team.name || team.shortName || '';
}

/**
 * A pick split into the bet itself and the matchup context around it, so a caller can
 * give them different visual weight: the row title leads with `pick` and trails the
 * `opponent` muted. Joining them back with a space gives the plain one-line label.
 */
export type PickParts = {
  /** The bet: team + line, the game total, or the prop text. */
  pick: string;
  /** "vs Ravens" / "at Chiefs" on a side; empty for totals, props and futures. */
  opponent: string;
};

export function formatReleasePickParts(
  release: PickRelease,
  nameMode: NameMode = 'long',
): PickParts {
  const {
    sportId,
    playType,
    sideIndicator,
    side,
    totalIndicator,
    total,
    period,
    price = -110,
  } = release;

  const periodTxt = period === 1 ? '1H' : period === 2 ? '2H' : '';
  // Only surface a non-standard price (e.g. a bought-on -3.5 (+100)); the default
  // -110 is implied and would just add noise.
  const priceTxt = price === -110 ? '' : `(${(price ?? 0) > 0 ? '+' : ''}${price})`;

  let spread = `${side}`;
  if (side === 0) {
    spread = 'PK';
  } else if (side && side > 0) {
    spread = `+${side}`;
  }

  const hname = teamLabel(release.home, nameMode, sportId);
  const vname = teamLabel(release.visitor, nameMode, sportId);

  const tidy = (txt: string) => txt.replace(/ +/g, ' ').trim();
  const parts = (pick: string, opponent = ''): PickParts => ({
    pick: tidy(pick),
    opponent: tidy(opponent),
  });

  if (playType === 'prop' || playType === 'future') {
    return parts(`${release.playText || ''} ${priceTxt}`);
  }
  if (sideIndicator === 'home') {
    // Home pick: the opponent is the visitor, and the pick is "vs" them.
    return parts(`${hname} ${spread} ${priceTxt} ${periodTxt}`, vname ? `vs ${vname}` : '');
  }
  if (sideIndicator === 'visitor') {
    // Road pick: the opponent is the home team, and the pick is "at" them.
    return parts(`${vname} ${spread} ${priceTxt} ${periodTxt}`, hname ? `at ${hname}` : '');
  }
  if (totalIndicator === 'over') {
    return parts(
      `${vname}${hname && vname ? ' / ' : ''}${hname} Over ${total} ${periodTxt} ${priceTxt}`,
    );
  }
  if (totalIndicator === 'under') {
    return parts(
      `${vname}${hname && vname ? ' / ' : ''}${hname} Under ${total} ${periodTxt} ${priceTxt}`,
    );
  }
  return parts('');
}

/** The whole pick as one string — the parts joined, for plain-text callers. */
export default function formatReleasePick(
  release: PickRelease,
  nameMode: NameMode = 'long',
): string {
  const { pick, opponent } = formatReleasePickParts(release, nameMode);
  return opponent ? `${pick} ${opponent}` : pick;
}
